import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Copy, Check, FileText, Image as ImageIcon, BookOpen, HeartHandshake, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

/* Public press kit — fact sheet, boilerplate, founder story, brand assets,
   and a one-click zip for journalists and creators. */

const EASE = [0.16, 1, 0.3, 1];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: EASE } });

const FACTS = [
  ["Title", "New Citizen (Early Access Alpha)"],
  ["Developer", "Specpixel Entertainment PTY LTD — Melbourne, Australia"],
  ["Engine", "Unreal Engine 5"],
  ["Platform", "PC (Steam)"],
  ["Genre", "Hardcore roleplay sandbox · living city"],
  ["Players", "Online multiplayer — official + community servers"],
  ["Languages", "EN · ES · DE · FR · PT-BR · RU (menu, tablet & phone)"],
  ["Funding", "100% self-funded by founder Tanner Rozankovic"],
  ["Contact", "business@specpixel.com"],
];

const BOILERPLATE = "New Citizen is a hardcore roleplay sandbox set in a living Australian city, built in Unreal Engine 5. Players create a citizen and pick a life — police dispatch on a real CAD tablet, fire rescue, the courthouse, player-elected mayors with real tax levers, phone banking, a stock market, and the underworld: safecracking, keypad hacks and casino vault heists where every silent alarm lands on a player-run dispatch board. The Gilded Spade casino runs roulette, slots and blackjack against real in-game bank balances, and CivCoin mining rigs turn heat management into a business. Server owners run their own worlds with an auto-detecting database bridge and JSON mod packs — themes, penal codes, departments, whole government systems.";

const LOGOS = [
  { file: "newcitizen-wordmark.png", label: "New Citizen wordmark" },
  { file: "newcitizen-logo-blue.png", label: "New Citizen mark — blue" },
  { file: "newcitizen-logo-red.png", label: "New Citizen mark — red" },
  { file: "specpixel-logo.png", label: "Specpixel logo" },
  { file: "specpixel-logo-transparent.png", label: "Specpixel logo — transparent" },
  { file: "og-image.png", label: "Key art (OG)" },
];

/* exact Steamworks capsule sizes — rendered server-side from the brand assets */
const CAPSULES = [
  { name: "header_capsule_460x215", label: "Header capsule", size: "460 × 215" },
  { name: "small_capsule_231x87", label: "Small capsule", size: "231 × 87" },
  { name: "main_capsule_616x353", label: "Main capsule", size: "616 × 353" },
  { name: "library_capsule_600x900", label: "Library capsule", size: "600 × 900" },
  { name: "library_hero_3840x1240", label: "Library hero", size: "3840 × 1240" },
  { name: "library_logo_1280x720", label: "Library logo", size: "1280 × 720" },
];

function CopyBlock({ testid, title, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5 relative">
      <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-3">{title}</p>
      <p className="font-mono text-[11px] text-zinc-300 leading-relaxed pr-10">{text}</p>
      <button data-testid={testid} onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-100 transition-colors" title="Copy">
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function PressKitPage() {
  return (
    <div data-testid="presskit-page" className="min-h-screen bg-[#07080a] text-zinc-100 flex flex-col">
      <Seo title="Press Kit — New Citizen | Specpixel Entertainment" description="Fact sheet, brand assets and the founder story for New Citizen — the self-funded living-city roleplay game in Unreal Engine 5." />
      <SiteNav />

      <header className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20">
          <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.5em] uppercase text-nc-orange mb-4 flex items-center gap-2"><FileText className="w-4 h-4" /> Press kit</motion.p>
          <motion.h1 {...fade(0.08)} className="font-sans font-black uppercase tracking-tight text-4xl sm:text-6xl leading-[1.05]">New Citizen</motion.h1>
          <motion.p {...fade(0.16)} className="font-mono text-xs sm:text-sm text-zinc-400 leading-relaxed max-w-2xl mt-5">
            Everything you need to cover the city: the fact sheet, the boilerplate, brand assets, and the founder story —
            a game entirely self-funded by a disability support worker's wages.
          </motion.p>
          <motion.div {...fade(0.24)} className="mt-8 flex gap-3 flex-wrap">
            <a data-testid="presskit-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/presskit/zip`}
              className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-7 py-3.5 inline-flex items-center gap-2 transition-colors"
              style={{ boxShadow: "0 0 24px rgba(255,107,26,0.25)" }}>
              <Download className="w-4 h-4" /> Download full kit (.zip)
            </a>
            <Link to="/story" data-testid="presskit-story-link" className="border border-white/20 hover:border-white/50 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-7 py-3.5 inline-flex items-center gap-2 transition-colors">
              <HeartHandshake className="w-4 h-4 text-amber-400" /> Founder story
            </Link>
          </motion.div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14 w-full flex-1 flex flex-col gap-14">
        {/* fact sheet */}
        <motion.section {...fade(0)} data-testid="presskit-facts">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-5 flex items-center gap-2"><BookOpen className="w-4 h-4 text-nc-blue" /> Fact sheet</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {FACTS.map(([k, v]) => (
              <div key={k} className="bg-[#0a0a0a] px-5 py-4">
                <p className="font-mono text-[8px] tracking-[0.3em] uppercase text-zinc-500">{k}</p>
                <p className="font-sans font-semibold text-sm text-zinc-100 mt-1.5 leading-snug">{v}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* boilerplate */}
        <motion.section {...fade(0)} className="flex flex-col gap-4">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">Boilerplate — paste-ready</p>
          <CopyBlock testid="presskit-copy-boilerplate" title="Long boilerplate (store pages, articles)" text={BOILERPLATE} />
          <CopyBlock testid="presskit-copy-founder" title="Founder funding note (human angle)"
            text="New Citizen is entirely self-funded by founder Tanner Rozankovic, who finances development from his wages working as a disability support worker — no publisher, no investors. 'Every street in this city was paid for one shift at a time.'" />
        </motion.section>

        {/* assets */}
        <motion.section {...fade(0)}>
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-5 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-nc-blue" /> Brand assets — click to download</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {LOGOS.map((l) => (
              <a key={l.file} data-testid={`presskit-asset-${l.file.replace(/\./g, "-")}`} href={`/assets/${l.file}`} download
                className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-col items-center gap-4 hover:border-nc-orange/50 transition-colors group">
                <img src={`/assets/${l.file}`} alt={l.label} className="h-16 object-contain" />
                <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-zinc-500 group-hover:text-zinc-200 transition-colors flex items-center gap-1.5">
                  <Download className="w-3 h-3" /> {l.label}
                </span>
              </a>
            ))}
          </div>
          <p className="font-mono text-[9px] text-zinc-600 mt-3 uppercase tracking-[0.15em]">In-game screenshots and b-roll: request via business@specpixel.com — captured fresh per build.</p>
        </motion.section>

        {/* steam capsules — exact Steamworks sizes, generated from the brand assets */}
        <motion.section {...fade(0)} data-testid="presskit-capsules">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-5">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 flex items-center gap-2"><ImageIcon className="w-4 h-4 text-nc-orange" /> Steam capsule set — exact Steamworks sizes</p>
            <a data-testid="capsules-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/presskit/steam-capsules.zip`}
              className="border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 inline-flex items-center gap-2 transition-colors">
              <Download className="w-3.5 h-3.5" /> Capsule set (.zip)
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAPSULES.map((c) => (
              <a key={c.name} data-testid={`capsule-${c.name}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/presskit/capsules/${c.name}.png`} target="_blank" rel="noreferrer"
                className="border border-white/10 bg-[#0a0a0a] p-4 flex flex-col gap-3 hover:border-nc-orange/50 transition-colors group">
                <div className="bg-black/60 border border-white/5 flex items-center justify-center overflow-hidden min-h-[90px]">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/presskit/capsules/${c.name}.png`} alt={`${c.label} — ${c.size}`}
                    className="max-h-28 w-auto object-contain group-hover:scale-[1.02] transition-transform" loading="lazy" />
                </div>
                <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-zinc-500 group-hover:text-zinc-200 transition-colors flex items-center justify-between">
                  <span className="flex items-center gap-1.5"><Download className="w-3 h-3" /> {c.label}</span>
                  <span className="text-zinc-600">{c.size}</span>
                </span>
              </a>
            ))}
          </div>
          <p className="font-mono text-[9px] text-zinc-600 mt-3 uppercase tracking-[0.15em]">Rendered from the brand assets at exact pixel size — upload straight into Steamworks → Graphical Assets.</p>
        </motion.section>

        {/* story cross-link */}
        <motion.section {...fade(0)} className="border border-amber-500/25 bg-amber-500/[0.04] p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-amber-400 mb-2">The human angle</p>
            <p className="font-sans font-bold text-xl text-zinc-50 leading-snug">"Every street in this city was paid for one shift at a time."</p>
            <p className="font-mono text-[10px] text-zinc-400 mt-3 leading-relaxed">The full founder story — the day job, the night shift, and why early access here means founding citizenship.</p>
          </div>
          <Link to="/story" data-testid="presskit-story-cta" className="shrink-0 px-6 py-3 font-sans font-semibold uppercase tracking-[0.2em] text-xs border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-2">
            Read it <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.section>
      </main>

      <SiteFooter />
    </div>
  );
}
